import { useCallback, useEffect, useRef, useState } from 'react';
import Dialog from 'components/Overlays/AnimatedDialog';
import { FiArrowUpRight, FiSearch, FiUser, FiX } from 'react-icons/fi';
import { Link, useHistory, useLocation } from 'react-router-dom';
import useMenuItems from './useMenuItems';
import usePortalDialog from './usePortalDialog';

export default function PortalPageSearch() {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const triggerRef = useRef(null), dialogRef = useRef(null);
    const bindDialog = useCallback((node) => { dialogRef.current = node; node?.querySelector('input')?.focus(); }, []);
    const location = useLocation();
    const history = useHistory();
    const items = useMenuItems();
    const close = useCallback(() => setOpen(false), []);
    usePortalDialog(open, close, dialogRef, triggerRef);
    useEffect(() => { close(); }, [location.pathname, location.search, close]);
    useEffect(() => {
        const onKeyDown = (event) => {
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k' && !document.querySelector('[aria-modal="true"]')) {
                event.preventDefault(); setOpen(true);
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, []);
    const results = [...items.flatMap((item) => item.children || [item]), { label: 'Your profile', to: '/my-account', icon: <FiUser /> }].filter((item) => item.label.toLowerCase().includes(query.trim().toLowerCase()));
    const navigateResults = (event) => {
        if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
        const links = Array.from(dialogRef.current?.querySelectorAll('nav a') || []);
        if (!links.length) return;
        event.preventDefault();
        const index = links.indexOf(document.activeElement);
        const next = event.key === 'ArrowDown' ? Math.min(index + 1, links.length - 1) : index - 1;
        if (next < 0) dialogRef.current?.querySelector('input')?.focus(); else links[next]?.focus();
    };
    return <>
        <button type="button" ref={triggerRef} className="portal-page-search-trigger" onClick={() => setOpen(true)} aria-label="Find a portal page" aria-haspopup="dialog" aria-keyshortcuts="Control+K Meta+K"><FiSearch aria-hidden="true" /><span>Find a page</span><kbd>⌘ / Ctrl K</kbd></button>
        <Dialog visible={open} onHide={close} onExited={() => setQuery('')} modal blockScroll dismissableMask focusOnShow={false} closable={false} draggable={false} resizable={false} position="top" className="portal-command-dialog" maskClassName="portal-command-mask" header={<h2 id="portal-command-title">Go to a page</h2>}>
            <div ref={bindDialog} className="portal-command-body" onKeyDown={navigateResults}>
                <button type="button" className="portal-icon-button portal-command-close" aria-label="Close page search" onClick={close}><FiX aria-hidden="true" /></button>
                <label className="portal-command-input"><FiSearch aria-hidden="true" /><input aria-label="Search portal pages" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search books, users, settings…" type="search" onKeyDown={(event) => { if (event.key === "Enter" && results.length) { event.preventDefault(); history.push(results[0].to); close(); } }} /></label>
                <nav aria-label="Page search results">{results.map((item) => <Link key={item.to} to={item.to} onClick={close}><span aria-hidden="true">{item.icon}</span><span>{item.label}</span><FiArrowUpRight aria-hidden="true" /></Link>)}</nav>
                {!results.length && <p className="portal-command-empty" role="status">No pages match “{query}”. Try another name.</p>}
                <footer><span>Only pages available to your account are shown.</span><kbd>Esc to close</kbd></footer>
            </div>
        </Dialog>
    </>;
}
